import {ReportTaxonomy, ReportTaxonomyField, fieldByFilterField, fieldByKey} from '../contracts/report-taxonomy.types';

/** Wire filter tokens the project-status catalog no longer advertises, mapped to the catalog key
 * that still describes them.
 *
 * These five fields are applied correctly by the engine but have no control in the interface, so
 * the catalog stopped publishing a `filterField` for them. Dropping the token also dropped the only
 * route from a STORED condition back to its field: reports authored while they were advertised
 * still exist and still render, and `fieldByFilterField` cannot resolve them any more.
 *
 * Without this map the interface falls back to showing the raw token - a reader sees
 * `TOTAL_REGISTERED_HOURS` as a field name, which is the exact outcome the authoring agent grew its
 * own unfilterable-key list to avoid. The catalog entries themselves are unchanged and still carry
 * the human label, so the lookup goes by key.
 *
 * Nothing may be ADDED here to hide a field from authoring: narrowing the catalog is what does
 * that. This exists solely so conditions written before the narrowing keep reading correctly. */
const CATALOG_KEY_BY_RETIRED_FILTER_FIELD: Record<string, string> = {
	TOTAL_REGISTERED_HOURS: 'projectStatus.hours.total',
	BILLABLE_HOURS: 'projectStatus.hours.billable',
	NON_BILLABLE_HOURS: 'projectStatus.hours.nonBillable',
	RETAINER_PERIOD_START: 'projectStatus.retainer.periodStart',
	RETAINER_PERIOD_END: 'projectStatus.retainer.periodEnd',
};

/** The catalog entry describing a stored condition's field, including one whose filter token the
 * catalog has retired.
 *
 * Resolves by `filterField` first, so an advertised field costs exactly what it did before, and
 * falls back to the retired-token map. Returns undefined for a token from neither, which stays the
 * caller's cue to show the token itself rather than a blank. */
export function fieldForStoredCondition(taxonomy: ReportTaxonomy, filterField: string): ReportTaxonomyField | undefined {
	const advertised = fieldByFilterField(taxonomy, filterField);
	if (advertised) {
		return advertised;
	}
	const retiredKey = CATALOG_KEY_BY_RETIRED_FILTER_FIELD[filterField];
	return retiredKey ? fieldByKey(taxonomy, retiredKey) : undefined;
}
